import React, { useState } from "react";
import { CheckCircle2, XCircle, Menu, X, ArrowRight } from "lucide-react";

const FEATURES = [
  { name: "Athlete + Parent Account Access", free: true, paid: true },
  { name: "Coach / Admin Dashboard", free: true, paid: true },
  { name: "SWOT Analysis + Season Priorities", free: true, paid: true },
  { name: "Progress Tracking + Accountability", free: true, paid: true },
  { name: "Program-Level Visibility", free: true, paid: true },
  { name: "Who Pays for Athlete Skill Workouts", free: "Athlete: $2/workout", paid: "Program" },
  { name: "Who Pays for Athlete Strength and Conditioning Workouts", free: "Athlete: $10/month", paid: "Program" },
];

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const brandLogo = `${process.env.PUBLIC_URL}/NuvioLogo.png?v=20260314c`;

  const navLinks = [
    {
      name: "About",
      href: "/home/about",
      children: [{ name: "Why Nuvio?", href: "/home/why-nuvio" }],
    },
    { name: "For Coaches", href: "/home/programs" },
    { name: "For Athletes", href: "/home/athletes" },
    { name: "Features", href: "/home/features" },
    { name: "Pricing", href: "/home/pricing" },
    { name: "Case Studies", href: "/home/case-studies" },
  ];

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-100">
      <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
        <a href="/home" className="flex items-center gap-2">
          <img
            src={brandLogo}
            alt="Nuvio"
            className="w-8 h-8 rounded-lg object-cover shadow-sm"
          />
          <span className="text-xl font-bold tracking-tight text-slate-900">
            Nuvio<span className="text-blue-600">Sport</span>
          </span>
        </a>

        <div className="hidden lg:flex items-center gap-8">
          {navLinks.map((link) =>
            link.children ? (
              <div key={link.name} className="relative group">
                <a
                  href={link.href}
                  className="text-sm font-semibold text-slate-600 hover:text-blue-600 transition-colors"
                >
                  {link.name}
                </a>
                <div className="absolute left-0 top-full pt-3 opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-all">
                  <div className="bg-white border border-slate-100 shadow-xl rounded-xl p-3 min-w-[180px]">
                    {link.children.map((child) => (
                      <a
                        key={child.name}
                        href={child.href}
                        className="block px-3 py-2 text-sm font-semibold text-slate-700 rounded-lg hover:bg-slate-50 hover:text-blue-600"
                      >
                        {child.name}
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <a
                key={link.name}
                href={link.href}
                className={`text-sm font-semibold transition-colors ${
                  link.name === "Pricing"
                    ? "text-blue-600"
                    : "text-slate-600 hover:text-blue-600"
                }`}
              >
                {link.name}
              </a>
            )
          )}
          <a href="/login" className="text-sm font-bold text-slate-700 hover:text-blue-600">
            Log In
          </a>
          <a
            href="/create-account"
            className="px-6 py-2.5 rounded-lg bg-slate-900 text-white text-sm font-bold hover:bg-slate-800 transition-all shadow-lg shadow-slate-900/20"
          >
            Create Account
          </a>
        </div>

        <button
          className="lg:hidden text-slate-700 p-2 hover:bg-slate-50 rounded-lg transition-colors"
          onClick={() => setIsOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {isOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      <div
        className={`lg:hidden absolute top-20 left-0 w-full bg-white border-b border-slate-100 transition-all duration-300 ease-in-out overflow-hidden ${
          isOpen ? "max-h-[420px] opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <div className="px-6 py-8 flex flex-col gap-6 shadow-xl">
          {navLinks.map((link) => (
            <div key={link.name} className="flex flex-col gap-2">
              <a
                href={link.href}
                onClick={() => setIsOpen(false)}
                className={`text-lg font-bold ${
                  link.name === "Pricing" ? "text-blue-600" : "text-slate-900"
                }`}
              >
                {link.name}
              </a>
              {link.children && (
                <div className="ml-4 flex flex-col gap-2">
                  {link.children.map((child) => (
                    <a
                      key={child.name}
                      href={child.href}
                      onClick={() => setIsOpen(false)}
                      className="text-sm font-semibold text-slate-600 hover:text-blue-600"
                    >
                      {child.name}
                    </a>
                  ))}
                </div>
              )}
            </div>
          ))}
          <hr className="border-slate-100" />
          <a
            href="/login"
            onClick={() => setIsOpen(false)}
            className="w-full py-4 text-center font-bold text-slate-700 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors"
          >
            Log In
          </a>
          <a
            href="/create-account"
            onClick={() => setIsOpen(false)}
            className="w-full py-4 text-center font-bold text-white bg-blue-600 rounded-xl shadow-lg shadow-blue-600/20 hover:bg-blue-700 transition-all"
          >
            Create Account
          </a>
        </div>
      </div>
    </nav>
  );
};

const PlanCard = ({ title, subtitle, accentClass }) => (
  <div className={`rounded-2xl border p-6 ${accentClass}`}>
    <p className="text-xs uppercase tracking-[0.18em] font-bold text-slate-500 mb-2">{title}</p>
    <p className="text-lg font-bold text-slate-900">{subtitle}</p>
  </div>
);

const FeatureCell = ({ value }) => {
  if (value === true) {
    return <CheckCircle2 className="text-green-600 mx-auto" size={20} />;
  }
  if (value === false) {
    return <XCircle className="text-slate-300 mx-auto" size={20} />;
  }
  return <span className="text-xs md:text-sm font-semibold text-slate-700">{value}</span>;
};

export default function PricingPage() {
  const [selectedTier, setSelectedTier] = useState("0-50");

  const tierPrices = {
    "0-50": "$750/year",
    "51-100": "$1,250/year",
    "101-200": "$2,250/year",
    "201-500": "$3,000/year",
    "500+": "Contact us",
  };

  return (
    <div className="min-h-screen bg-white text-slate-900">
      <Navbar />

      <section className="pt-36 pb-16 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-blue-50 border border-blue-100 text-blue-700 text-xs font-bold uppercase tracking-widest mb-6">
              Pricing
            </div>
            <h1 className="text-4xl md:text-6xl font-extrabold tracking-tight mb-4">
              Same Platform. Different Payer Model.
            </h1>
            <p className="text-slate-500 text-lg max-w-3xl mx-auto">
              Every feature stays available in both tiers. The only difference is who pays for
              the workouts.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-4 mb-10">
            <PlanCard
              title="Free Tier"
              subtitle="Athletes pay for skill + strength workouts"
              accentClass="bg-slate-50 border-slate-200"
            />
            <PlanCard
              title="Paid Tier"
              subtitle="Programs cover athlete workout costs"
              accentClass="bg-blue-50 border-blue-200"
            />
          </div>

          <div className="rounded-2xl border border-slate-200 overflow-hidden">
            <div className="grid grid-cols-[1.5fr_0.75fr_0.75fr] bg-slate-100 px-4 md:px-6 py-4 text-xs md:text-sm font-bold uppercase tracking-wider text-slate-600">
              <div>Feature</div>
              <div className="text-center">Free</div>
              <div className="text-center">Paid</div>
            </div>

            <div className="grid grid-cols-[1.5fr_0.75fr_0.75fr] px-4 md:px-6 py-4 border-t border-slate-100 items-center gap-2">
              <div className="text-sm md:text-base font-medium text-slate-800 pr-2"># of athletes</div>
              <div className="text-center text-xs md:text-sm font-semibold text-slate-700">Unlimited</div>
              <div className="text-center">
                <select
                  value={selectedTier}
                  onChange={(e) => setSelectedTier(e.target.value)}
                  className="w-full max-w-[180px] bg-white border border-slate-200 rounded-lg px-3 py-2 text-xs md:text-sm font-semibold text-slate-700 text-center focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                >
                  <option value="0-50">0 - 50</option>
                  <option value="51-100">51 - 100</option>
                  <option value="101-200">101 - 200</option>
                  <option value="201-500">201 - 500</option>
                  <option value="500+">500+</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-[1.5fr_0.75fr_0.75fr] px-4 md:px-6 py-4 border-t border-slate-100 items-center gap-2">
              <div className="text-sm md:text-base font-medium text-slate-800 pr-2">Price</div>
              <div className="text-center text-xs md:text-sm font-semibold text-slate-700">Free for Programs</div>
              <div className="text-center">
                <span className="text-xs md:text-sm font-semibold text-slate-700">
                  {tierPrices[selectedTier]}
                </span>
              </div>
            </div>

            {FEATURES.map((feature) => (
              <div
                key={feature.name}
                className="grid grid-cols-[1.5fr_0.75fr_0.75fr] px-4 md:px-6 py-4 border-t border-slate-100 items-center gap-2"
              >
                <div className="text-sm md:text-base font-medium text-slate-800 pr-2">{feature.name}</div>
                <div className="text-center">
                  <FeatureCell value={feature.free} />
                </div>
                <div className="text-center">
                  <FeatureCell value={feature.paid} />
                </div>
              </div>
            ))}

            <div className="grid grid-cols-[1.5fr_0.75fr_0.75fr] px-4 md:px-6 py-5 border-t border-slate-100 items-center gap-2 bg-slate-50/60">
              <div className="text-sm md:text-base font-medium text-slate-800 pr-2">Get Started</div>
              <div className="text-center">
                <a
                  href="/create-account"
                  className="inline-flex items-center justify-center px-4 py-2 rounded-lg bg-slate-900 text-white text-xs md:text-sm font-bold hover:bg-slate-800 transition-all"
                >
                  Create Account
                </a>
              </div>
              <div className="text-center">
                <a
                  href="/login?next=/admin/profile&intent=upgrade"
                  className="inline-flex items-center justify-center px-4 py-2 rounded-lg bg-blue-600 text-white text-xs md:text-sm font-bold hover:bg-blue-700 transition-all"
                >
                  Upgrade
                </a>
              </div>
            </div>
          </div>

          <div className="mt-10 rounded-2xl border border-amber-200 bg-amber-50 p-6">
            <h2 className="text-xl font-bold text-slate-900 mb-2">Tiered Program Pricing</h2>
            <p className="text-slate-700">
              Paid plans scale with roster size. Larger clubs with more athletes move into higher
              pricing tiers because the program is covering more athlete workouts.
            </p>
            <p className="text-slate-600 text-sm mt-2">
              Example structure: small roster tier, mid-size tier, and large club tier.
            </p>
          </div>

          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="/create-account"
              className="w-full sm:w-auto px-8 py-4 rounded-xl bg-blue-600 text-white font-bold hover:bg-blue-700 transition-all flex items-center justify-center gap-2 shadow-xl shadow-blue-600/20"
            >
              Get Started <ArrowRight size={18} />
            </a>
            <a
              href="https://calendar.app.google/919vQjWVJnBKRPgA8"
              className="w-full sm:w-auto px-8 py-4 rounded-xl bg-white border-2 border-slate-200 text-slate-700 font-bold hover:bg-slate-50 transition-all"
            >
              Talk Through Tiers
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
